import type OlMap from "ol/Map";
import { fromLonLat } from "ol/proj";
import { useCallback, useEffect, useRef, useState } from "react";

import { fetchNgwLayerItemExtent } from "@nextgis/ngw-kit";
import type { NgwIdentifyEvent } from "@nextgis/ngw-map";
import { useNgwMapContext } from "@nextgis/react-ngw-map";
import type { MapClickEvent } from "@nextgis/webmap";

import { useTranslation } from "../../i18n";
import type { ToolMode } from "../../types";
import { removeLayerIfExists } from "../../utils/map";
import { MapTools } from "../MapTools";

import { IdentifyPopup } from "./IdentifyPopup";
import type { IdentifyFeatureItem, IdentifyPopupState } from "./types";

import "./MapIdentification.css";

type Bounds = [number, number, number, number];

function isLngLat(value: unknown): value is number[] {
  return (
    Array.isArray(value) &&
    value.length >= 2 &&
    Number.isFinite(value[0]) &&
    Number.isFinite(value[1])
  );
}

function toMapPosition(lngLat: unknown) {
  if (!isLngLat(lngLat)) return null;
  return fromLonLat(lngLat);
}

function getMapPosition(event: MapClickEvent) {
  return toMapPosition(event.lngLat);
}

function getIdentifyPosition(event: NgwIdentifyEvent) {
  const lngLat =
    event.sourceType === "vector"
      ? event.event.event.lngLat || event.event.getCenter()
      : event.event.lngLat;

  return toMapPosition(lngLat);
}

function isValidBounds(bounds: unknown): bounds is Bounds {
  if (!Array.isArray(bounds) || bounds.length < 4) return false;
  const [west, south, east, north] = bounds;

  return (
    [west, south, east, north].every(Number.isFinite) &&
    west >= -180 &&
    west <= 180 &&
    east >= -180 &&
    east <= 180 &&
    south >= -90 &&
    south <= 90 &&
    north >= -90 &&
    north <= 90 &&
    west <= east &&
    south <= north
  );
}

export function MapIdentification({
  activeMode,
  highlightLayerId,
}: {
  activeMode: ToolMode | null;
  highlightLayerId: string;
}) {
  const { t } = useTranslation();
  const { ngwMap } = useNgwMapContext<OlMap>();
  const olMap = ngwMap.mapAdapter?.map;
  const [identifyActive, setIdentifyActive] = useState(true);
  const [identifyPopup, setIdentifyPopup] = useState<IdentifyPopupState | null>(
    null,
  );
  const lastIdentifyPosition = useRef<number[] | null>(null);
  const identifyRequestId = useRef(0);

  const closeIdentifyPopup = useCallback(() => {
    identifyRequestId.current += 1;
    lastIdentifyPosition.current = null;
    setIdentifyPopup(null);
    ngwMap.cancelPromises("identify");
    removeLayerIfExists(ngwMap, highlightLayerId);
  }, [highlightLayerId, ngwMap]);

  const zoomToFeature = useCallback(
    async (item: IdentifyFeatureItem) => {
      const bounds = await fetchNgwLayerItemExtent({
        connector: ngwMap.connector,
        resourceId: item.layerId,
        featureId: item.id,
      });

      if (isValidBounds(bounds)) {
        ngwMap.fitBounds(bounds);
      }
    },
    [ngwMap],
  );

  useEffect(() => {
    if (activeMode) {
      setIdentifyActive(false);
    }
  }, [activeMode]);

  useEffect(() => {
    if (!identifyActive) {
      return;
    }

    const onPreClick = (event: MapClickEvent) => {
      const position = getMapPosition(event);
      if (!position) return;

      identifyRequestId.current += 1;
      lastIdentifyPosition.current = position;
      ngwMap.cancelPromises("select", "identify");
      removeLayerIfExists(ngwMap, highlightLayerId);
      setIdentifyPopup({
        loading: true,
        message: t("identify.identifying"),
        items: [],
        position,
      });
    };

    const onSelect = async (event: NgwIdentifyEvent | null) => {
      const position = event
        ? getIdentifyPosition(event)
        : lastIdentifyPosition.current;

      if (!position) {
        setIdentifyPopup(null);
        return;
      }

      if (!event) {
        setIdentifyPopup({
          loading: false,
          message: t("identify.noFeature"),
          items: [],
          position,
        });
        return;
      }

      const requestId = ++identifyRequestId.current;
      const items = event.getIdentifyItems();
      setIdentifyPopup({
        loading: false,
        message: items.length ? "" : t("identify.noFeature"),
        items,
        position,
      });

      try {
        const geojson = await ngwMap.fetchIdentifyGeoJson(event, {
          multiple: true,
        });
        if (identifyRequestId.current !== requestId) {
          return;
        }
        if (geojson) {
          removeLayerIfExists(ngwMap, highlightLayerId);
          await ngwMap.addGeoJsonLayer({
            id: highlightLayerId,
            data: geojson,
            paint: {
              type: "path",
              color: "#5d8f13",
              strokeColor: "#242f21",
              strokeOpacity: 0.95,
              fillColor: "#93c13d",
              fillOpacity: 0.18,
              weight: 3,
            },
          });
        }
      } catch {
        if (identifyRequestId.current === requestId) {
          setIdentifyPopup({
            loading: false,
            message: t("identify.failed"),
            items: [],
            position,
          });
        }
      }
    };

    ngwMap.emitter.on("preclick", onPreClick);
    ngwMap.emitter.on("ngw:select", onSelect);

    return () => {
      ngwMap.emitter.removeListener("preclick", onPreClick);
      ngwMap.emitter.removeListener("ngw:select", onSelect);
    };
  }, [highlightLayerId, identifyActive, ngwMap, t]);

  useEffect(() => {
    if (identifyActive) {
      ngwMap.enableSelection();
      return;
    }

    ngwMap.disableSelection();
    ngwMap.cancelPromises("select", "identify");
    removeLayerIfExists(ngwMap, highlightLayerId);
    identifyRequestId.current += 1;
    lastIdentifyPosition.current = null;
    setIdentifyPopup(null);
  }, [highlightLayerId, identifyActive, ngwMap]);

  return (
    <>
      <MapTools
        key={activeMode ?? "map-tools"}
        identifyActive={identifyActive}
        onIdentifyChange={setIdentifyActive}
      />
      {olMap && identifyPopup && (
        <IdentifyPopup
          map={olMap}
          identify={identifyPopup}
          onClose={closeIdentifyPopup}
          onZoomToFeature={zoomToFeature}
        />
      )}
    </>
  );
}
